import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';
import {
  Users, Clock, UserX, Timer, ClipboardEdit, X, Check, Wifi,
  Download, FileText, Search, Filter, CalendarDays,
  AlertCircle, Building2, ArrowRightLeft
} from 'lucide-react';
import {
  format, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  startOfYear, endOfYear, getYear, getMonth,
} from 'date-fns';
import { useAttendanceSummary, useDailyAttendance, useMonthlyAllAttendance, useRegularizeAttendance, useClearAttendanceDate, useClearPunchLogsDate } from '@/api/queries/attendance.queries';
import { useEmployees, useDepartments, useDesignations } from '@/api/queries/employee.queries';
import { useLeaveBalances, useSubmitLeave, useLeaveRequests } from '@/api/queries/leave.queries';
import { useCompanySettings } from '@/api/queries/settings.queries';
import { useSwitchCompany } from '@/api/queries/workspace.queries';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PageHeader } from '@/components/shared/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AttendanceExportModal } from '@/components/attendance/AttendanceExportModal';
import { selectOwnedCompanies, selectActiveCompanyId, setOwnedCompanies, setActiveCompanyId } from '@/store/slices/workspaceSlice';
import { selectCompanyPlan, selectCompanyName, setCompany } from '@/store/slices/companySlice';
import { setAuth } from '@/store/slices/authSlice';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/cn';

// ─── Constants ───────────────────────────────────────────────────────────────
const ALL_STATUSES = ['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'LEAVE', 'HOLIDAY', 'WEEKLY_OFF'];
const MARK_STATUSES = ALL_STATUSES;
const STATUS_TABS   = ['All', ...ALL_STATUSES];

const LEAVE_TYPE_STYLES = {
  EL: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
  CL: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300',
  SL: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300',
};
const LEAVE_TYPE_LABELS = { EL: 'Earned Leave', CL: 'Casual Leave', SL: 'Sick Leave' };
const LEAVE_TYPE_TO_FULL = { EL: 'EARNED_LEAVE', CL: 'CASUAL_LEAVE', SL: 'SICK_LEAVE' };
const FALLBACK_LEAVE_TYPES = [
  { key: 'EL', label: 'Earned Leave',  remaining: 12 },
  { key: 'CL', label: 'Casual Leave',  remaining: 8  },
  { key: 'SL', label: 'Sick Leave',    remaining: 6  },
];

const VIEW_MODES = [
  { key: 'today',      label: 'Today'      },
  { key: 'this_week',  label: 'This Week'  },
  { key: 'this_month', label: 'This Month' },
  { key: 'monthly',    label: 'Monthly'    },
  { key: 'yearly',     label: 'Yearly'     },
  { key: 'custom',     label: 'Custom'     },
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const YEARS  = Array.from({ length: 5 }, (_, i) => getYear(new Date()) - i);

const DEFAULT_SHIFTS = [
  { id: 'morning', name: 'Morning Shift' },
  { id: 'evening', name: 'Evening Shift' },
  { id: 'night',   name: 'Night Shift'   },
];

// ─── Utilities ───────────────────────────────────────────────────────────────
function getDateRange(mode, customFrom, customTo, selectedMonth, selectedYear) {
  const now = new Date();
  switch (mode) {
    case 'today':      return { from: format(now, 'yyyy-MM-dd'), to: format(now, 'yyyy-MM-dd') };
    case 'this_week':  return { from: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'), to: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
    case 'this_month': return { from: format(startOfMonth(now), 'yyyy-MM-dd'), to: format(endOfMonth(now), 'yyyy-MM-dd') };
    case 'monthly': {
      const d = new Date(selectedYear, selectedMonth, 1);
      return { from: format(startOfMonth(d), 'yyyy-MM-dd'), to: format(endOfMonth(d), 'yyyy-MM-dd') };
    }
    case 'yearly': {
      const d = new Date(selectedYear, 0, 1);
      return { from: format(startOfYear(d), 'yyyy-MM-dd'), to: format(endOfYear(d), 'yyyy-MM-dd') };
    }
    case 'custom':
    default: return { from: customFrom, to: customTo };
  }
}

// ─── Chip ─────────────────────────────────────────────────────────────────────
function Chip({ label, onRemove }) {
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] text-[11px] font-medium">
      {label}
      <button onClick={onRemove} className="hover:opacity-70"><X size={9}/></button>
    </span>
  );
}

// ─── Device Notice Banner ─────────────────────────────────────────────────────
function DeviceNoticeBanner() {
  const key = 'attn_device_banner_dismissed';
  const [visible, setVisible] = useState(() => !sessionStorage.getItem(key));
  if (!visible) return null;
  const dismiss = () => { sessionStorage.setItem(key, '1'); setVisible(false); };
  return (
    <div className="flex items-start gap-3 px-4 py-3 rounded-[12px] border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-[13px] text-blue-700 dark:text-blue-300">
      <Wifi size={16} className="shrink-0 mt-0.5"/>
      <p className="flex-1">
        <span className="font-semibold">Biometric & kiosk devices integration coming soon — </span>
        currently running on Manual, GPS & QR check-in only. Your existing attendance records are safe.
      </p>
      <button onClick={dismiss} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity"><X size={14}/></button>
    </div>
  );
}

// Default leave balances — shown immediately while API loads or if service is unavailable
const DEFAULT_BAL = { EL: 13, CL: 9, SL: 11, CO: 2 };

// ─── Mark Attendance Modal ────────────────────────────────────────────────────
function MarkAttendanceModal({ employee, date, existing, onClose }) {
  const regularize  = useRegularizeAttendance();
  const submitLeave = useSubmitLeave();
  const [status, setStatus]       = useState(existing?.status || 'PRESENT');
  const [checkIn, setCheckIn]     = useState(existing?.check_in || '09:00');
  const [checkOut, setCheckOut]   = useState(existing?.check_out || '18:00');
  const [leaveType, setLeaveType] = useState('EL');
  const [success, setSuccess]     = useState(false);

  const needsTimes = ['PRESENT', 'LATE', 'HALF_DAY'].includes(status);
  const isLeave    = status === 'LEAVE';
  const inputCls   = "w-full h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[8px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const empId = employee?.employee_id || employee?.id;
  const { data: balData } = useLeaveBalances(empId);
  // Balance lookup: used to overlay remaining days on buttons (optional — buttons always show)
  const balByCode = useMemo(() => {
    const out = {};
    (balData?.data || []).forEach(b => { if (b.leave_code) out[b.leave_code] = b; });
    return out;
  }, [balData]);

  const handleSubmit = async () => {
    try {
      const empIdStr = employee.employee_id || employee.id;
      const empName  = employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim();
      const deptStr  = typeof employee.department === 'string' ? employee.department : (employee.department?.name || '');
      const desigStr = typeof employee.designation === 'string' ? employee.designation : (employee.designation?.name || '');

      // 1. Mark attendance
      await regularize.mutateAsync({
        employee_id:    empIdStr,
        employee_code:  employee.employee_code || '',
        employee_name:  empName,
        department:     deptStr,
        designation:    desigStr,
        date,
        status,
        check_in:    needsTimes ? checkIn  : null,
        check_out:   needsTimes ? checkOut : null,
        leave_type:  isLeave    ? leaveType : null,
      });

      // 2. If marking as leave, also create a PENDING leave request in leave-service
      if (isLeave && leaveType) {
        await submitLeave.mutateAsync({
          employee_id:    empIdStr,
          employee_name:  empName,
          employee_code:  employee.employee_code || '',
          department:     deptStr,
          designation:    desigStr,
          leave_type:     LEAVE_TYPE_TO_FULL[leaveType] || leaveType,
          from_date:      date,
          to_date:        date,
          days:           1,
          reason:         'Attendance regularized by HR',
          source:         'ATTENDANCE_REGULARIZE',
        });
      }

      setSuccess(true);
      setTimeout(onClose, 900);
    } catch (err) {
      alert(err?.response?.data?.error?.message || 'Failed to mark attendance');
    }
  };

  const name = employee.name || `${employee.first_name} ${employee.last_name}`;
  const isSaving = regularize.isPending || submitLeave.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-sm border border-[var(--border)] fade-in overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[var(--border)] shrink-0">
          <div className="flex items-center gap-3">
            <Avatar name={name} src={employee.photo_url || null} size="sm"/>
            <div>
              <p className="text-[14px] font-bold text-[var(--text-primary)]">{name}</p>
              <p className="text-[12px] text-[var(--text-tertiary)]">{date}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={15}/></button>
        </div>

        <div className="px-5 py-5 space-y-4 overflow-y-auto">
          {success ? (
            <div className="flex flex-col items-center py-4 gap-2">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check size={24} className="text-green-600"/>
              </div>
              <p className="text-[14px] font-bold text-[var(--text-primary)]">
                {isLeave ? 'Attendance marked · Leave request sent for approval' : 'Attendance Marked'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-[12px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Status</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {MARK_STATUSES.map(s => (
                    <button key={s} onClick={() => setStatus(s)}
                      className={cn(
                        'py-1.5 px-1 rounded-[8px] text-[10px] font-semibold transition-all',
                        status === s
                          ? 'bg-[var(--accent)] text-white'
                          : 'bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
                      )}>
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {needsTimes && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[var(--text-secondary)]">Check In</label>
                    <input type="time" value={checkIn} onChange={e => setCheckIn(e.target.value)} className={inputCls}/>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[var(--text-secondary)]">Check Out</label>
                    <input type="time" value={checkOut} onChange={e => setCheckOut(e.target.value)} className={inputCls}/>
                  </div>
                </div>
              )}

              {isLeave && (
                <div className="space-y-2">
                  <p className="text-[12px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Leave Type</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'EL', label: 'Earned Leave'  },
                      { key: 'CL', label: 'Casual Leave'  },
                      { key: 'SL', label: 'Sick Leave'    },
                    ].map(lt => {
                      const bal = balByCode[lt.key];
                      const avail = bal != null ? bal.available : (DEFAULT_BAL[lt.key] ?? '?');
                      const sel = leaveType === lt.key;
                      return (
                        <button
                          type="button"
                          key={lt.key}
                          onClick={e => { e.stopPropagation(); setLeaveType(lt.key); }}
                          className={cn(
                            'flex flex-col items-center py-3 px-1 rounded-[12px] border-2 transition-all',
                            sel
                              ? 'border-[var(--accent)] bg-[var(--accent)]/5'
                              : 'border-[var(--border)] bg-[var(--bg-muted)] hover:border-[var(--accent)]/50'
                          )}>
                          <span className={`text-[16px] font-black leading-none ${sel ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]'}`}>
                            {lt.key}
                          </span>
                          <span className="text-[9px] text-[var(--text-secondary)] text-center leading-tight mt-1">
                            {lt.label}
                          </span>
                          <span className={`text-[10px] font-semibold mt-1 ${avail === 0 ? 'text-red-500' : sel ? 'text-[var(--accent)]' : 'text-[var(--text-tertiary)]'}`}>
                            {avail}d left
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-[var(--text-tertiary)]">A leave request will be created and sent for approval.</p>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button onClick={handleSubmit} loading={isSaving} className="flex-1" size="sm">
                  Save Attendance
                </Button>
                <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Per-employee leave balance row (one hook call per component, rules-safe) ──
function EmpLeaveBalRow({ emp, leaveType }) {
  const empId = emp.employee_id || emp.id;
  const { data, isLoading } = useLeaveBalances(empId);
  const balances = data?.data || [];
  const bal = balances.find(b => b.leave_code === leaveType);
  // Use actual balance if loaded, fallback to default so it never shows "—"
  const available = bal != null ? bal.available : (DEFAULT_BAL[leaveType] ?? '?');
  const name = emp.name || emp.employee_name || `${emp.first_name||''} ${emp.last_name||''}`.trim();
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-[var(--border)] last:border-0 text-[12px]">
      <span className="text-[var(--text-primary)] truncate max-w-[65%]">{name}</span>
      <span className={available === 0 ? 'text-red-500 font-bold' : 'text-[var(--text-secondary)]'}>
        {isLoading ? '...' : `${available}d left`}
      </span>
    </div>
  );
}

// ─── Bulk Mark Modal ──────────────────────────────────────────────────────────
function BulkMarkModal({ employees, date, onClose }) {
  const regularize = useRegularizeAttendance();
  const [status, setStatus]     = useState('PRESENT');
  const [checkIn, setCheckIn]   = useState('09:00');
  const [checkOut, setCheckOut] = useState('18:00');
  const [leaveType, setLeaveType] = useState('EL');
  const [saving, setSaving]     = useState(false);
  const [done, setDone]         = useState(false);

  const needsTimes = ['PRESENT', 'LATE', 'HALF_DAY'].includes(status);
  const isLeave    = status === 'LEAVE';
  const inputCls   = "w-full h-9 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[8px] text-[13px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";

  const handleBulkMark = async () => {
    setSaving(true);
    try {
      await Promise.all(employees.map(e =>
        regularize.mutateAsync({
          employee_id:   e.employee_id || e.id,
          employee_code: e.employee_code || '',
          employee_name: e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim(),
          department:    typeof e.department === 'string' ? e.department : (e.department?.name || ''),
          designation:   typeof e.designation === 'string' ? e.designation : (e.designation?.name || ''),
          date,
          status,
          check_in:    needsTimes ? checkIn  : null,
          check_out:   needsTimes ? checkOut : null,
          leave_type:  isLeave    ? leaveType : null,
        })
      ));
      setDone(true);
      setTimeout(onClose, 1000);
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-sm border border-[var(--border)] fade-in">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[var(--border)]">
          <div>
            <p className="text-[14px] font-bold text-[var(--text-primary)]">Mark All Attendance</p>
            <p className="text-[12px] text-[var(--text-tertiary)]">{employees.length} employees · {date}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={15}/></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          {done ? (
            <div className="flex flex-col items-center py-4 gap-2">
              <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Check size={24} className="text-green-600"/>
              </div>
              <p className="text-[14px] font-bold text-[var(--text-primary)]">All marked as {status}</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-[12px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Mark all as</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {MARK_STATUSES.map(s => (
                    <button key={s} onClick={() => setStatus(s)}
                      className={cn(
                        'py-1.5 px-1 rounded-[8px] text-[10px] font-semibold transition-all',
                        status === s ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
                      )}>
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
              {needsTimes && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[var(--text-secondary)]">Check In</label>
                    <input type="time" value={checkIn} onChange={e => setCheckIn(e.target.value)} className={inputCls}/>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[12px] font-semibold text-[var(--text-secondary)]">Check Out</label>
                    <input type="time" value={checkOut} onChange={e => setCheckOut(e.target.value)} className={inputCls}/>
                  </div>
                </div>
              )}
              {isLeave && (
                <div className="space-y-2">
                  <p className="text-[12px] font-semibold text-[var(--text-secondary)] uppercase tracking-wide">Leave Type</p>
                  <div className="space-y-1.5">
                    {[{ key: 'EL', label: 'Earned Leave' }, { key: 'CL', label: 'Casual Leave' }, { key: 'SL', label: 'Sick Leave' }].map(lt => (
                      <button key={lt.key} onClick={() => setLeaveType(lt.key)}
                        className={cn('w-full flex items-center justify-between px-3 py-2 rounded-[10px] border-2 transition-all text-left',
                          leaveType === lt.key ? 'border-[var(--accent)] bg-[var(--accent)]/5' : 'border-[var(--border)] hover:border-[var(--accent)]/40')}>
                        <span className="text-[13px] font-semibold text-[var(--text-primary)]">{lt.key} <span className="font-normal text-[var(--text-secondary)]">— {lt.label}</span></span>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${LEAVE_TYPE_STYLES[lt.key] || 'bg-gray-50 border-gray-200 text-gray-700'}`}>{lt.key}</span>
                      </button>
                    ))}
                  </div>
                  {employees.length > 0 && (
                    <div className="rounded-[10px] border border-[var(--border)] overflow-hidden">
                      <div className="px-3 py-1.5 bg-[var(--bg-muted)] border-b border-[var(--border)]">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">{leaveType} balance — selected employees</p>
                      </div>
                      <div className="px-3 py-1 max-h-36 overflow-y-auto">
                        {employees.slice(0, 10).map(emp => (
                          <EmpLeaveBalRow key={emp.employee_id || emp.id} emp={emp} leaveType={leaveType} />
                        ))}
                        {employees.length > 10 && <p className="text-[10px] text-[var(--text-tertiary)] py-1.5">+{employees.length - 10} more employees</p>}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button onClick={handleBulkMark} loading={saving} className="flex-1" size="sm">
                  Mark {employees.length} Employees
                </Button>
                <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AttendancePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const ownedCompanies  = useSelector(selectOwnedCompanies);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const plan            = useSelector(selectCompanyPlan);
  const companyName     = useSelector(selectCompanyName);
  const isCustomMulti   = plan === 'CUSTOM' && ownedCompanies.length > 1;

  const { mutate: switchCompany, isPending: switching } = useSwitchCompany();
  const [switchTarget, setSwitchTarget] = useState(null); // company object to confirm switch to

  const handleCompanySwitch = (company) => {
    if (company.id === activeCompanyId) return;
    setSwitchTarget(company);
  };

  const confirmSwitch = () => {
    if (!switchTarget) return;
    switchCompany(switchTarget.id, {
      onSuccess: (res) => {
        const { access_token, user, company, permissions, owned_companies } = res.data;
        dispatch(setAuth({ user, companyId: company.id, accessToken: access_token, permissions }));
        dispatch(setCompany(company));
        dispatch(setOwnedCompanies(owned_companies || ownedCompanies));
        dispatch(setActiveCompanyId(company.id));
        queryClient.clear();
        setSwitchTarget(null);
        toast.success(`Switched to ${company.name}`);
        navigate('/attendance');
      },
      onError: () => toast.error('Failed to switch company'),
    });
  };

  // ── Date view state ──────────────────────────────────────────────────────
  const now = new Date();
  const [viewMode, setViewMode]           = useState('today');
  const [customFrom, setCustomFrom]       = useState(format(now, 'yyyy-MM-dd'));
  const [customTo, setCustomTo]           = useState(format(now, 'yyyy-MM-dd'));
  const [selectedMonth, setSelectedMonth] = useState(getMonth(now));
  const [selectedYear, setSelectedYear]   = useState(getYear(now));

  const { from: dateFrom, to: dateTo } = getDateRange(viewMode, customFrom, customTo, selectedMonth, selectedYear);
  const isToday = viewMode === 'today';
  const today   = format(now, 'EEEE, d MMM yyyy');
  const todayDate = format(now, 'yyyy-MM-dd');

  // ── Filter state ─────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter]   = useState('All');
  const [searchQuery, setSearchQuery]     = useState('');
  const [deptFilter, setDeptFilter]       = useState('');
  const [desigFilter, setDesigFilter]     = useState('');
  const [typeFilter, setTypeFilter]       = useState('');
  const [shiftFilter, setShiftFilter]     = useState('');

  // ── Modal state ──────────────────────────────────────────────────────────
  const [markingEmp, setMarkingEmp]   = useState(null);
  const [showBulk, setShowBulk]       = useState(false);
  const [showExport, setShowExport]   = useState(false);

  // ── Row selection for bulk marking ───────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll    = () => setSelectedIds(prev => prev.size === filtered.length && filtered.length > 0 ? new Set() : new Set(filtered.map(r => r.employee_id)));
  const selCount     = selectedIds.size;

  // ── Clear today (dev utility) ────────────────────────────────────────────
  const clearAtt   = useClearAttendanceDate();
  const clearPunch = useClearPunchLogsDate();
  const handleClearToday = async () => {
    if (!window.confirm(`Clear ALL attendance + punch records for ${todayDate}? This cannot be undone.`)) return;
    await Promise.all([
      clearAtt.mutateAsync(todayDate),
      clearPunch.mutateAsync(todayDate),
    ]);
    toast.success(`Cleared all records for ${todayDate} — you can now mark fresh attendance`);
  };

  // ── Data queries ─────────────────────────────────────────────────────────
  const { data: summaryResp } = useAttendanceSummary(todayDate, activeCompanyId);
  const s = summaryResp?.data || {};

  // Fetch all leave requests so the table can show pending/approved status on LEAVE rows
  const { data: leaveReqData } = useLeaveRequests({});
  const allLeaveRequests = leaveReqData?.data || [];

  const { data: dailyResp } = useDailyAttendance({ date: isToday ? todayDate : dateFrom }, activeCompanyId);
  const allRecords = dailyResp?.data || [];

  const { data: empResp }   = useEmployees();
  const allEmployees        = empResp?.data || [];

  // ── Range view: per-employee monthly summary ─────────────────────────────
  const rangeMonth = dateFrom.slice(0, 7); // 'YYYY-MM'
  const { data: monthlyAllResp } = useMonthlyAllAttendance(
    !isToday ? rangeMonth : null,
    {},
    activeCompanyId,
    !isToday ? allEmployees : []
  );

  // ── Unified table rows ───────────────────────────────────────────────────
  // TODAY  → ALL employees merged with daily records (unmarked = NOT_MARKED)
  // RANGE  → per-employee P/A/L summary over the selected date range
  const tableRows = useMemo(() => {
    const deptStr = (d) => typeof d === 'string' ? d : (d?.name || '');
    if (isToday) {
      const recordMap = new Map((allRecords || []).map(r => [r.employee_id, r]));
      return allEmployees.map(emp => {
        const empId   = emp.id || emp.employee_id;
        const empName = emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
        const base    = { employee_id: empId, employee_code: emp.employee_code, name: empName, department: deptStr(emp.department), designation: deptStr(emp.designation), employment_type: emp.employment_type };
        const rec     = recordMap.get(empId);
        if (rec) return { ...base, ...rec, name: rec.name || empName };
        return { ...base, status: 'NOT_MARKED', check_in: null, check_out: null, hours_worked: null, ot_minutes: 0, method: null, checkout_pending: false };
      });
    }

    // Range: build per-employee day-count summary
    const samMonth  = dateFrom.slice(0, 7) === dateTo.slice(0, 7);
    const monthData = monthlyAllResp?.data || [];
    const today     = new Date().toISOString().slice(0, 10);
    const fromDay   = parseInt(dateFrom.slice(8));
    const toDay     = parseInt(dateTo.slice(8));

    const empList = monthData.length > 0 ? monthData : allEmployees.map(emp => ({
      employee_id:    emp.id || emp.employee_id,
      employee_code:  emp.employee_code,
      name:           emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
      department:     deptStr(emp.department),
      designation:    deptStr(emp.designation),
      employment_type: emp.employment_type,
      days: {}, times: {},
    }));

    return empList.map(emp => {
      let P = 0, A = 0, L = 0, S = 0, OT_min = 0;
      if (samMonth) {
        for (let d = fromDay; d <= toDay; d++) {
          const key     = String(d);
          const dayDate = `${rangeMonth}-${String(d).padStart(2, '0')}`;
          const st      = emp.days?.[key];
          const times   = emp.times?.[key];
          if (st === 'S' || st === 'H') { S++; continue; }
          if (!st) { if (dayDate <= today) A++; continue; }
          if (st === 'P') P++;
          else if (st === 'A') A++;
          else if (st === 'L') L++;
          if (times?.ot_min) OT_min += times.ot_min;
        }
      }
      const rangeOT = OT_min > 0 ? `${Math.floor(OT_min / 60)}h ${OT_min % 60}m` : '0h 0m';
      return {
        employee_id: emp.employee_id, employee_code: emp.employee_code, name: emp.name,
        department: emp.department, designation: emp.designation, employment_type: emp.employment_type,
        rangeP: P, rangeA: A, rangeL: L, rangeS: S, rangeOT,
        crossMonth: !samMonth,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, allEmployees, allRecords, monthlyAllResp, dateFrom, dateTo, rangeMonth]);

  // Build an employee_id → leave requests lookup for the status badge
  const leaveReqByEmp = useMemo(() => {
    const map = {};
    allLeaveRequests.forEach(req => {
      const eid = req.employee?.id;
      if (eid) { map[eid] = map[eid] || []; map[eid].push(req); }
    });
    return map;
  }, [allLeaveRequests]);

  const photoByName = useMemo(() => {
    const m = {};
    allEmployees.forEach(e => {
      if (e.photo_url) {
        m[`${e.first_name} ${e.last_name}`] = e.photo_url;
        if (e.employee_code) m[e.employee_code] = e.photo_url;
      }
    });
    return m;
  }, [allEmployees]);

  const { data: deptsResp } = useDepartments();
  const departments         = deptsResp?.data || [];

  const { data: desigResp } = useDesignations();
  const designations        = desigResp?.data || [];

  const { data: settingsResp } = useCompanySettings();
  const configuredShifts = settingsResp?.data?.settings?.shifts || DEFAULT_SHIFTS;

  // ── KPI counts ───────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { PRESENT:0, ABSENT:0, LATE:0, HALF_DAY:0, LEAVE:0, HOLIDAY:0, WEEKLY_OFF:0 };
    if (isToday) {
      tableRows.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
      // If truly no records exist at all, fallback to API summary
      const hasAnyRecord = tableRows.some(r => r.status !== 'NOT_MARKED');
      if (!hasAnyRecord) {
        c.PRESENT = s.present || 0;
        c.ABSENT  = s.absent  || 0;
        c.LATE    = s.late_arrivals || s.late || 0;
        c.LEAVE   = s.on_leave || 0;
      }
    } else {
      // Range: aggregate across employees
      tableRows.forEach(r => {
        c.PRESENT  += r.rangeP || 0;
        c.ABSENT   += r.rangeA || 0;
        c.LEAVE    += r.rangeL || 0;
      });
    }
    return c;
  }, [isToday, tableRows, s]);

  // ── Filtered rows ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let rows = [...tableRows];
    // Status filter only meaningful in TODAY view
    if (isToday && statusFilter !== 'All') rows = rows.filter(r => r.status === statusFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter(r => r.name?.toLowerCase().includes(q) || r.employee_code?.toLowerCase().includes(q));
    }
    if (deptFilter)  rows = rows.filter(r => (r.department?.name || r.department) === deptFilter);
    if (desigFilter) rows = rows.filter(r => r.designation === desigFilter);
    if (typeFilter)  rows = rows.filter(r => r.employment_type === typeFilter);
    if (isToday && shiftFilter) rows = rows.filter(r => (r.shift || '').includes(shiftFilter));
    return rows;
  }, [tableRows, isToday, statusFilter, searchQuery, deptFilter, desigFilter, typeFilter, shiftFilter]);

  const filterSummary = [
    viewMode !== 'today' && `Period: ${dateFrom}${dateTo !== dateFrom ? ` to ${dateTo}` : ''}`,
    isCustomMulti && `Company: ${companyName}`,
    statusFilter !== 'All' && `Status: ${statusFilter}`,
    searchQuery && `Search: "${searchQuery}"`,
    deptFilter && `Dept: ${deptFilter}`,
    desigFilter && `Designation: ${desigFilter}`,
    typeFilter && `Type: ${typeFilter}`,
    shiftFilter && `Shift: ${shiftFilter}`,
  ].filter(Boolean).join(' · ');

  const hasFilters = statusFilter !== 'All' || searchQuery || deptFilter || desigFilter || typeFilter || shiftFilter;
  const clearFilters = () => { setStatusFilter('All'); setSearchQuery(''); setDeptFilter(''); setDesigFilter(''); setTypeFilter(''); setShiftFilter(''); };

  const kpiCards = [
    { key: 'PRESENT',    title: 'Present',    value: counts.PRESENT,    icon: Check,    color: 'green'  },
    { key: 'ABSENT',     title: 'Absent',     value: counts.ABSENT,     icon: UserX,    color: 'red'    },
    { key: 'LATE',       title: 'Late',       value: counts.LATE,       icon: Timer,    color: 'orange' },
    { key: 'HALF_DAY',   title: 'Half Day',   value: counts.HALF_DAY,   icon: Clock,    color: 'yellow' },
    { key: 'LEAVE',      title: 'On Leave',   value: counts.LEAVE,      icon: Users,    color: 'blue'   },
    { key: 'HOLIDAY',    title: 'Holiday',    value: counts.HOLIDAY,    icon: CalendarDays, color: 'purple' },
    { key: 'WEEKLY_OFF', title: 'Weekly Off', value: counts.WEEKLY_OFF, icon: AlertCircle,  color: 'gray'   },
  ];

  const goToReport = () => navigate(`/reports?type=attendance_detail&from=${dateFrom}&to=${dateTo}${deptFilter ? `&dept=${encodeURIComponent(deptFilter)}` : ''}`);

  const inputCls = "h-9 px-3 text-[13px] bg-transparent text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none transition-colors";
  const selectCls = "h-9 px-2.5 text-[13px] bg-transparent text-[var(--text-primary)] focus:outline-none cursor-pointer";

  return (
    <div className="space-y-5 fade-in">

      {/* Page header */}
      <PageHeader
        title="Attendance"
        subtitle={isToday ? `Live register · ${today}` : `${dateFrom}${dateTo !== dateFrom ? ` to ${dateTo}` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" icon={FileText} onClick={goToReport} className="text-[var(--text-secondary)]">
              Report
            </Button>
            <Button variant="outline" size="sm" icon={Download} onClick={() => setShowExport(true)}>Export</Button>
            <Button variant="outline" size="sm" icon={ClipboardEdit} onClick={() => setShowBulk(true)}>
              {selCount > 0 ? `Mark Selected (${selCount})` : 'Mark All'}
            </Button>
            {isToday && (
              <button
                onClick={handleClearToday}
                disabled={clearAtt.isPending || clearPunch.isPending}
                className="h-8 px-3 text-[12px] font-medium text-red-600 border border-red-300 rounded-[8px] hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                title="Dev tool: clear today's attendance + punch records"
              >
                Clear Today
              </button>
            )}
          </div>
        }
      />

      {/* Device notice banner */}
      <DeviceNoticeBanner/>

      {/* ── Controls card ─────────────────────────────────────────────────── */}
      <div className="card p-0 overflow-hidden space-y-0">

        {/* Company context bar — CUSTOM plan with multiple companies only */}
        {isCustomMulti && (
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--border)] bg-[var(--bg-muted)]">
            <Building2 size={14} className="text-[var(--text-tertiary)] shrink-0"/>
            <span className="text-[12px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide mr-1">Workspace</span>
            <div className="flex gap-1.5 flex-wrap">
              {ownedCompanies.map(c => (
                <button key={c.id}
                  onClick={() => handleCompanySwitch(c)}
                  disabled={switching}
                  className={cn('px-3 py-1 rounded-full text-[12px] font-semibold transition-colors',
                    c.id === activeCompanyId
                      ? 'bg-[var(--accent)] text-white cursor-default'
                      : 'bg-white dark:bg-[var(--bg-card)] text-[var(--text-secondary)] border border-[var(--border)] hover:border-[var(--accent)]/60 hover:text-[var(--accent)]')}>
                  {c.id === activeCompanyId ? c.name : <span className="flex items-center gap-1">{c.name} <ArrowRightLeft size={10}/></span>}
                </button>
              ))}
            </div>
            <span className="ml-auto text-[11px] text-[var(--text-tertiary)]">Showing {companyName} data</span>
          </div>
        )}

        {/* Date view mode tabs */}
        <div className="flex items-center gap-0 border-b border-[var(--border)] overflow-x-auto">
          {VIEW_MODES.map(vm => (
            <button key={vm.key} onClick={() => setViewMode(vm.key)}
              className={cn(
                'px-4 py-2.5 text-[13px] font-semibold whitespace-nowrap transition-colors border-b-2',
                viewMode === vm.key
                  ? 'border-[var(--accent)] text-[var(--accent)]'
                  : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)]'
              )}>
              {vm.label}
            </button>
          ))}
        </div>

        {/* Date picker row — adapts to view mode */}
        {viewMode !== 'today' && viewMode !== 'this_week' && viewMode !== 'this_month' && (
          <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--border)] bg-[var(--bg-muted)]">
            {viewMode === 'monthly' && (
              <>
                <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
                  className="h-8 px-2 rounded-[8px] border border-[var(--border)] text-[13px] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]">
                  {MONTHS.map((m,i) => <option key={i} value={i}>{m}</option>)}
                </select>
                <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
                  className="h-8 px-2 rounded-[8px] border border-[var(--border)] text-[13px] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]">
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </>
            )}
            {viewMode === 'yearly' && (
              <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
                className="h-8 px-2 rounded-[8px] border border-[var(--border)] text-[13px] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]">
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {viewMode === 'custom' && (
              <div className="flex items-center gap-2">
                <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="h-8 px-2 rounded-[8px] border border-[var(--border)] text-[13px] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"/>
                <span className="text-[13px] text-[var(--text-tertiary)]">to</span>
                <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="h-8 px-2 rounded-[8px] border border-[var(--border)] text-[13px] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"/>
              </div>
            )}
            <span className="text-[12px] text-[var(--text-tertiary)] ml-auto">
              {dateFrom}{dateTo !== dateFrom ? ` → ${dateTo}` : ''}
            </span>
          </div>
        )}

        {/* Employee filter strip */}
        <div className="flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-[var(--border)]">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"/>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name or employee code…"
              className={`w-full ${inputCls} pl-8`}/>
          </div>
          <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)} className={selectCls}>
            <option value="">All Departments</option>
            {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
          <select value={desigFilter} onChange={e => setDesigFilter(e.target.value)} className={selectCls}>
            <option value="">All Designations</option>
            {designations.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={selectCls}>
            <option value="">All Types</option>
            <option value="FULL_TIME">Full Time</option>
            <option value="PART_TIME">Part Time</option>
            <option value="CONTRACT">Contract</option>
            <option value="DAILY_WAGE">Daily Wage</option>
          </select>
          <select value={shiftFilter} onChange={e => setShiftFilter(e.target.value)} className={selectCls}>
            <option value="">All Shifts</option>
            {configuredShifts.map(sh => <option key={sh.id || sh.name} value={sh.name}>{sh.name}</option>)}
          </select>
        </div>

        {/* Active filter chips */}
        {hasFilters && (
          <div className="flex flex-wrap items-center gap-1.5 px-3.5 py-2 border-t border-[var(--border)] bg-[var(--bg-muted)]">
            <Filter size={11} className="text-[var(--text-tertiary)]"/>
            {statusFilter !== 'All' && <Chip label={statusFilter.replace(/_/g,' ')} onRemove={()=>setStatusFilter('All')}/>}
            {searchQuery && <Chip label={`"${searchQuery}"`} onRemove={()=>setSearchQuery('')}/>}
            {deptFilter && <Chip label={deptFilter} onRemove={()=>setDeptFilter('')}/>}
            {desigFilter && <Chip label={desigFilter} onRemove={()=>setDesigFilter('')}/>}
            {typeFilter && <Chip label={typeFilter} onRemove={()=>setTypeFilter('')}/>}
            {shiftFilter && <Chip label={shiftFilter} onRemove={()=>setShiftFilter('')}/>}
            <button onClick={clearFilters} className="ml-auto text-[11px] text-[var(--text-tertiary)] hover:text-[var(--danger)] flex items-center gap-0.5">
              <X size={10}/> Clear all
            </button>
            <span className="text-[11px] text-[var(--text-tertiary)]">· {filtered.length} records</span>
          </div>
        )}
      </div>

      {/* ── KPI cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {kpiCards.map(card => (
          <button key={card.key}
            onClick={() => setStatusFilter(statusFilter === card.key ? 'All' : card.key)}
            className={cn(
              'card flex flex-col items-center py-3 px-2 transition-all cursor-pointer',
              statusFilter === card.key ? 'ring-2 ring-[var(--accent)]' : 'hover:ring-1 hover:ring-[var(--border)]'
            )}>
            <p className={`text-[22px] font-black ${
              card.color === 'green'  ? 'text-green-600'  :
              card.color === 'red'    ? 'text-red-500'    :
              card.color === 'orange' ? 'text-orange-500' :
              card.color === 'yellow' ? 'text-yellow-500' :
              card.color === 'blue'   ? 'text-blue-500'   :
              card.color === 'purple' ? 'text-purple-500' :
              'text-[var(--text-tertiary)]'
            }`}>{card.value}</p>
            <p className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide text-center leading-tight">{card.title}</p>
          </button>
        ))}
      </div>

      {/* Empty state — today only, when nobody is marked yet */}
      {isToday && tableRows.every(r => r.status === 'NOT_MARKED') && allEmployees.length > 0 && (
        <div className="card border border-[var(--accent)]/30 bg-[var(--accent)]/5 flex items-center justify-between p-5">
          <div>
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">No attendance recorded today</p>
            <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">Mark attendance for your {allEmployees.length} employee{allEmployees.length !== 1 ? 's' : ''}.</p>
          </div>
          <Button size="sm" onClick={() => setShowBulk(true)}>Mark Attendance</Button>
        </div>
      )}

      {/* ── Status tabs — only for Today view ───────────────────────────────── */}
      {isToday && (
        <div className="flex gap-1.5 overflow-x-auto pb-1 flex-wrap">
          {STATUS_TABS.map(t => {
            const cnt = t === 'All'
              ? tableRows.length
              : tableRows.filter(r => r.status === t).length;
            return (
              <button key={t} onClick={() => setStatusFilter(t)}
                className={cn('px-3 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors',
                  statusFilter === t ? 'bg-[var(--accent)] text-white' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]'
                )}>
                {t === 'All' ? 'All' : t.replace(/_/g, ' ')} ({cnt})
              </button>
            );
          })}
        </div>
      )}

      {/* ── TODAY: Desktop table ────────────────────────────────────────────── */}
      {isToday && (
        <div className="card p-0 overflow-x-auto hidden sm:block">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selCount > 0 && selCount === filtered.length}
                    ref={el => { if (el) el.indeterminate = selCount > 0 && selCount < filtered.length; }}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded cursor-pointer accent-[var(--accent)]"
                  />
                </th>
                {['Employee', 'Department', 'Check In', 'Check Out', 'Hours', 'Method', 'Status', ''].map(h => (
                  <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filtered.map(r => (
                <tr key={r.employee_id} className={cn('hover:bg-[var(--bg-muted)] transition-colors', r.status === 'NOT_MARKED' && 'opacity-55', selectedIds.has(r.employee_id) && 'bg-[var(--accent)]/5')}>
                  <td className="px-4 py-3.5">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.employee_id)}
                      onChange={() => toggleSelect(r.employee_id)}
                      className="w-4 h-4 rounded cursor-pointer accent-[var(--accent)]"
                    />
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={r.name} src={photoByName[r.name] || photoByName[r.employee_code] || null} size="sm"/>
                      <div>
                        <p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.name}</p>
                        <p className="text-[11px] text-[var(--text-tertiary)]">{r.employee_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{typeof r.department === 'string' ? (r.department || '—') : (r.department?.name || '—')}</td>
                  <td className="px-5 py-3.5 text-[13px] font-medium text-[var(--text-primary)]">{r.check_in || '—'}</td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">
                    {r.checkout_pending
                      ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-full">In Office</span>
                      : r.check_out || '—'}
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{r.hours_worked ?? '—'}</td>
                  <td className="px-5 py-3.5">
                    {r.method ? <Badge variant="default" size="sm">{r.method}</Badge> : <span className="text-[12px] text-[var(--text-tertiary)]">—</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    {r.status === 'NOT_MARKED'
                      ? <span className="text-[12px] text-[var(--text-tertiary)] italic">Not marked</span>
                      : (() => {
                          const isOnLeave = r.status === 'LEAVE' || r.status === 'ON_LEAVE';
                          const leaveReq = isOnLeave
                            ? (leaveReqByEmp[r.employee_id] || []).find(req => req.from_date <= todayDate && req.to_date >= todayDate)
                            : null;
                          return (
                            <div className="flex flex-col gap-0.5">
                              <StatusBadge status={r.status}/>
                              {isOnLeave && r.leave_type && (
                                <span className="text-[10px] text-[var(--text-tertiary)] font-medium">{r.leave_type}</span>
                              )}
                              {leaveReq?.status === 'PENDING' && (
                                <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400">Pending Approval</span>
                              )}
                              {leaveReq?.status === 'APPROVED' && (
                                <span className="text-[10px] font-semibold text-green-600 dark:text-green-400">Approved</span>
                              )}
                            </div>
                          );
                        })()
                    }
                  </td>
                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => setMarkingEmp({ ...r, status: r.status === 'NOT_MARKED' ? undefined : r.status })}
                      className="text-[11px] font-semibold text-[var(--accent)] hover:underline flex items-center gap-1">
                      <ClipboardEdit size={11}/> {r.status === 'NOT_MARKED' ? 'Mark' : 'Edit'}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">
                    No records match the current filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── TODAY: Mobile cards ─────────────────────────────────────────────── */}
      {isToday && (
        <div className="sm:hidden space-y-2">
          {filtered.map(r => (
            <div key={r.employee_id} className={cn('card flex items-center gap-3', r.status === 'NOT_MARKED' && 'opacity-55')}>
              <Avatar name={r.name} src={photoByName[r.name] || photoByName[r.employee_code] || null} size="md"/>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.name}</p>
                <p className="text-[11px] text-[var(--text-secondary)]">{r.department?.name || r.department}</p>
                {r.check_in && <p className="text-[11px] text-[var(--text-tertiary)]">In: {r.check_in}{r.check_out ? ` · Out: ${r.check_out}` : ''}</p>}
                {r.status === 'NOT_MARKED' && <p className="text-[11px] text-[var(--text-tertiary)] italic">Not marked yet</p>}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {r.status === 'NOT_MARKED'
                  ? <span className="text-[10px] text-[var(--text-tertiary)] italic">—</span>
                  : <StatusBadge status={r.status}/>
                }
                <button
                  onClick={() => setMarkingEmp({ ...r, status: r.status === 'NOT_MARKED' ? undefined : r.status })}
                  className="text-[10px] font-semibold text-[var(--accent)]">
                  {r.status === 'NOT_MARKED' ? 'Mark' : 'Edit'}
                </button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="card text-center py-8 text-[14px] text-[var(--text-tertiary)]">
              No employees found
            </div>
          )}
        </div>
      )}

      {/* ── RANGE: Summary table (week / month / custom) ─────────────────────── */}
      {!isToday && (
        <div className="card p-0 overflow-x-auto">
          {filtered.length > 0 && filtered[0]?.crossMonth && (
            <div className="px-5 py-3 bg-[var(--bg-muted)] border-b border-[var(--border)] text-[12px] text-[var(--text-tertiary)]">
              Multi-month range — showing employee list only. For day-by-day breakdown use <button onClick={goToReport} className="text-[var(--accent)] hover:underline font-medium">Reports</button>.
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border)]">
                {['Employee', 'Department', 'Present', 'Absent', 'Leave', 'Off / Holiday', 'Total OT'].map(h => (
                  <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filtered.map(r => (
                <tr key={r.employee_id} className="hover:bg-[var(--bg-muted)] transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <Avatar name={r.name} src={photoByName[r.name] || photoByName[r.employee_code] || null} size="sm"/>
                      <div>
                        <p className="text-[13px] font-semibold text-[var(--text-primary)]">{r.name}</p>
                        <p className="text-[11px] text-[var(--text-tertiary)]">{r.employee_code}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{typeof r.department === 'string' ? (r.department || '—') : (r.department?.name || '—')}</td>
                  <td className="px-5 py-3.5">
                    {r.crossMonth ? <span className="text-[var(--text-tertiary)]">—</span>
                      : <span className="text-[15px] font-bold text-green-600 dark:text-green-400">{r.rangeP}</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    {r.crossMonth ? <span className="text-[var(--text-tertiary)]">—</span>
                      : <span className={cn('text-[15px] font-bold', r.rangeA > 0 ? 'text-red-500' : 'text-[var(--text-tertiary)]')}>{r.rangeA}</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    {r.crossMonth ? <span className="text-[var(--text-tertiary)]">—</span>
                      : <span className="text-[15px] font-bold text-blue-500">{r.rangeL}</span>}
                  </td>
                  <td className="px-5 py-3.5 text-[13px] text-[var(--text-tertiary)]">
                    {r.crossMonth ? '—' : r.rangeS}
                  </td>
                  <td className="px-5 py-3.5 text-[13px] font-medium text-[var(--text-secondary)]">
                    {r.crossMonth ? '—' : r.rangeOT}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">
                    No employees found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {/* Floating selection bar */}
      {selCount > 0 && isToday && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 bg-[var(--bg-card)] border border-[var(--accent)]/40 rounded-[16px] shadow-[var(--shadow-xl)] fade-in">
          <span className="text-[13px] font-semibold text-[var(--text-primary)]">{selCount} employee{selCount > 1 ? 's' : ''} selected</span>
          <Button size="sm" icon={ClipboardEdit} onClick={() => setShowBulk(true)}>Mark Attendance</Button>
          <button onClick={() => setSelectedIds(new Set())} className="p-1.5 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)]"><X size={14}/></button>
        </div>
      )}

      {markingEmp && (
        <MarkAttendanceModal
          employee={markingEmp}
          date={isToday ? todayDate : dateFrom}
          existing={markingEmp}
          onClose={() => setMarkingEmp(null)}
        />
      )}

      {showBulk && (
        <BulkMarkModal
          employees={
            (selCount > 0
              ? filtered.filter(r => selectedIds.has(r.employee_id))
              : filtered
            ).map(e => ({ ...e, employee_id: e.employee_id || e.id }))
          }
          date={isToday ? todayDate : dateFrom}
          onClose={() => { setShowBulk(false); setSelectedIds(new Set()); }}
        />
      )}

      {showExport && (
        <AttendanceExportModal
          onClose={() => setShowExport(false)}
          records={filtered}
          filterSummary={filterSummary}
          companyName={companyName}
        />
      )}

      {/* Company switch confirmation */}
      {switchTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-card)] rounded-[16px] border border-[var(--border)] shadow-[var(--shadow-xl)] p-5 w-full max-w-sm fade-in">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-[10px] bg-[var(--accent)]/10 flex items-center justify-center shrink-0">
                <ArrowRightLeft size={16} className="text-[var(--accent)]"/>
              </div>
              <div>
                <p className="text-[14px] font-bold text-[var(--text-primary)]">Switch workspace?</p>
                <p className="text-[12px] text-[var(--text-tertiary)]">To view {switchTarget.name}'s attendance you need to switch workspace</p>
              </div>
            </div>
            <p className="text-[13px] text-[var(--text-secondary)] mb-4 pl-12">
              This will switch your active workspace to <strong>{switchTarget.name}</strong> and reload attendance data.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSwitchTarget(null)} className="flex-1">Cancel</Button>
              <Button onClick={confirmSwitch} loading={switching} className="flex-1">
                Switch to {switchTarget.name}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
